import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDiscrepancyLog, resolveDiscrepancy } from '../../../redux/slices/discrepancyLog';
import styles from './Discrepancies.module.css';
import TickBox from '../../../Components/TickBox/TickBox';
import Filter from '../../../Components/Filter/Filter';

function Discrepancies() {
  const dispatch = useDispatch();
  const { discrepancies, isLoading } = useSelector((state) => state.discrepancyLog);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  
  // Filter state
  const [filters, setFilters] = useState({
    issueType: 'All',
    status: 'All',
    location: 'All'
  });
  
  // Filtered and sorted data
  const [processedData, setProcessedData] = useState([]);

  useEffect(() => {
    dispatch(getDiscrepancyLog());
  }, [dispatch]);

  // Process data whenever discrepancies, sortConfig, or filters change
  useEffect(() => {
    let filteredData = [...discrepancies];

    // Apply filters
    if (filters.issueType !== 'All') {
      filteredData = filteredData.filter(item => item.issue_type === filters.issueType);
    }
    if (filters.status !== 'All') {
      filteredData = filteredData.filter(item => {
        const status = item.resolution_status || 'Unresolved';
        return status === filters.status;
      });
    }
    if (filters.location !== 'All') {
      filteredData = filteredData.filter(item => item.location === filters.location);
    }

    // Apply sorting
    if (sortConfig.key !== null) {
      filteredData.sort((a, b) => {
        let aVal = getSortValue(a, sortConfig.key);
        let bVal = getSortValue(b, sortConfig.key);

        if (aVal < bVal) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    setProcessedData(filteredData);
  }, [discrepancies, sortConfig, filters]);

  // Get unique values for filter options
  const getFilterOptions = (field) => {
    const uniqueValues = [...new Set(discrepancies.map(item => {
      if (field === 'status') {
        return item.resolution_status || 'Unresolved';
      }
      return item[field];
    }))].filter(Boolean);
    
    return [
      { name: 'All', value: 'All' },
      ...uniqueValues.map(value => ({ name: value, value: value }))
    ];
  };

  // Get sortable value for different column types
  const getSortValue = (item, columnKey) => {
    switch (columnKey) {
      case 'id':
        return parseInt(item.id) || 0;
      case 'check_timestamp':
      case 'resolution_timestamp':
        return item[columnKey] ? new Date(item[columnKey]).getTime() : 0;
      case 'status':
        return item.resolution_status || 'Unresolved';
      default:
        return item[columnKey] || '';
    }
  };

  // Handle column sorting
  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleResolve = (logId) => {
    dispatch(resolveDiscrepancy(logId));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Get sort arrow for column headers
  const getSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    }
    return '';
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Discrepancy Log</h1>
      
      {/* Filters */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Issue Type:</label>
          <Filter
            selectedOption={filters.issueType}
            Options={getFilterOptions('issue_type')}
            onSelect={(value) => handleFilterChange('issueType', value)}
            disabled={isLoading}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Status:</label>
          <Filter
            selectedOption={filters.status}
            Options={getFilterOptions('status')}
            onSelect={(value) => handleFilterChange('status', value)}
            disabled={isLoading}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Location:</label>
          <Filter
            selectedOption={filters.location}
            Options={getFilterOptions('location')}
            onSelect={(value) => handleFilterChange('location', value)}
            disabled={isLoading}
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className={styles.loading}>Loading discrepancies...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('id')}
                >
                  ID{getSortArrow('id')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('check_timestamp')}
                >
                  Check Time{getSortArrow('check_timestamp')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('issue_type')}
                >
                  Issue Type{getSortArrow('issue_type')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('identifier')}
                >
                  Identifier{getSortArrow('identifier')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('details')}
                >
                  Details{getSortArrow('details')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('location')}
                >
                  Location{getSortArrow('location')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('status')}
                >
                  Status{getSortArrow('status')}
                </th>
                <th 
                  className={styles.sortableHeader} 
                  onClick={() => handleSort('resolution_timestamp')}
                >
                  Resolution Time{getSortArrow('resolution_timestamp')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {processedData.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.noData}>No discrepancies found.</td>
                </tr>
              ) : (
                processedData.map((item) => (
                  <tr key={item.id} className={styles.row}>
                    <td>{item.id}</td>
                    <td>{formatDate(item.check_timestamp)}</td>
                    <td>{item.issue_type}</td>
                    <td>{item.identifier}</td>
                    <td>{item.details}</td>
                    <td>{item.location}</td>
                    <td className={
                      item.resolution_status === 'Resolved' 
                        ? styles.resolved 
                        : styles.unresolved
                    }>
                      {item.resolution_status || 'Unresolved'}
                    </td>
                    <td>{formatDate(item.resolution_timestamp)}</td>
                    <td>
                      {item.resolution_status !== 'Resolved' && (
                        <div className={styles.tickBoxContainer}>
                          <TickBox 
                            tickClick={() => handleResolve(item.id)} 
                            usable={true}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Discrepancies; 